import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { serverURL } from '../constants';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';
import { getSessionAuth, applySessionClaims } from '../shared/auth/session';
import { refreshAccessToken } from '../shared/auth/tokenRefresh';

async function fetchWithAuth(url) {
    let token = sessionStorage.getItem('token');
    if (!token) {
        token = await refreshAccessToken();
    }
    if (!token) {
        const err = new Error('No access token');
        err.code = 'NO_TOKEN';
        throw err;
    }

    const headers = { Authorization: `Bearer ${token}` };
    try {
        return await axios.get(url, { headers, timeout: 15000 });
    } catch (error) {
        if (error.response?.status !== 401) throw error;
        const next = await refreshAccessToken();
        if (!next) throw error;
        return axios.get(url, {
            headers: { Authorization: `Bearer ${next}` },
            timeout: 15000,
        });
    }
}

function sessionLooksLikeAdmin() {
    const auth = getSessionAuth();
    const type = sessionStorage.getItem('type');
    return Boolean(
        auth.platformAdmin
        || auth.role === 'admin'
        || type === 'main'
    );
}

const ProtectedAdminRoute = ({ children }) => {
    const { t } = useTranslation();
    const [isAdmin, setIsAdmin] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const checkAdminStatus = async () => {
            try {
                const email = sessionStorage.getItem('email');
                const auth = sessionStorage.getItem('auth');
                const uid = sessionStorage.getItem('uid');

                if (!email || auth !== 'true' || !uid) {
                    if (!cancelled) {
                        setIsAdmin(false);
                        setIsLoading(false);
                    }
                    return;
                }

                // Fast path: session already marked as platform admin with a token
                if (sessionLooksLikeAdmin() && sessionStorage.getItem('token')) {
                    if (!cancelled) {
                        setIsAdmin(true);
                        setIsLoading(false);
                    }
                    // Soft-verify claims in background; don't kick out on blips
                    fetchWithAuth(`${serverURL}/api/auth/me`)
                        .then((me) => {
                            const data = me.data || {};
                            applySessionClaims({
                                organizationId: data.organizationId,
                                orgRole: data.orgRole,
                                platformAdmin: data.platformAdmin,
                                role: data.user?.role,
                                email: data.user?.email || email,
                                mName: data.user?.mName,
                                uid: data.user?._id || uid,
                            });
                            if (data.platformAdmin || data.user?.role === 'admin') {
                                sessionStorage.setItem('platformAdmin', 'true');
                            }
                        })
                        .catch(() => { /* keep session fast-path */ });
                    return;
                }

                // Prefer /api/auth/me (sets platformAdmin from DB + Admin bridge)
                try {
                    const me = await fetchWithAuth(`${serverURL}/api/auth/me`);
                    const data = me.data || {};
                    applySessionClaims({
                        organizationId: data.organizationId,
                        orgRole: data.orgRole,
                        platformAdmin: data.platformAdmin,
                        role: data.user?.role,
                        email: data.user?.email || email,
                        mName: data.user?.mName,
                        uid: data.user?._id || uid,
                    });
                    const ok = Boolean(
                        data.platformAdmin
                        || data.user?.role === 'admin'
                        || data.user?.type === 'main'
                        || sessionLooksLikeAdmin()
                    );
                    if (!cancelled) {
                        if (ok) sessionStorage.setItem('platformAdmin', 'true');
                        setIsAdmin(ok);
                        setIsLoading(false);
                    }
                    return;
                } catch (meErr) {
                    if (meErr.code !== 'NO_TOKEN' && meErr.response?.status !== 401) {
                        logger.warn('ProtectedAdminRoute: /api/auth/me failed', meErr.message);
                    }
                }

                // Fallback: legacy admin email list endpoint
                try {
                    const response = await fetchWithAuth(`${serverURL}/api/admins`);
                    const data = response.data || {};
                    if (data.platformAdmin || data.isAdmin) {
                        sessionStorage.setItem('platformAdmin', 'true');
                        if (!cancelled) {
                            setIsAdmin(true);
                            setIsLoading(false);
                        }
                        return;
                    }
                    const adminEmails = data.adminEmails || [];
                    const isAdminUser = adminEmails.some(
                        (adminEmail) => String(adminEmail).toLowerCase() === email.toLowerCase()
                    );
                    if (isAdminUser) {
                        sessionStorage.setItem('platformAdmin', 'true');
                    }
                    if (!cancelled) {
                        setIsAdmin(isAdminUser || sessionLooksLikeAdmin());
                        setIsLoading(false);
                    }
                    return;
                } catch (adminErr) {
                    if (adminErr.response?.status === 401 || adminErr.code === 'NO_TOKEN') {
                        logger.warn('ProtectedAdminRoute: auth required — sign in again to refresh your token');
                    } else {
                        logger.warn('ProtectedAdminRoute: admin check failed', adminErr.message);
                    }
                }

                // Last resort: trust session flags (avoids kicking out after transient API blips)
                if (!cancelled) {
                    setIsAdmin(sessionLooksLikeAdmin());
                    setIsLoading(false);
                }
            } catch (error) {
                logger.warn('ProtectedAdminRoute: unexpected error', error?.message || error);
                if (!cancelled) {
                    setIsAdmin(sessionLooksLikeAdmin());
                    setIsLoading(false);
                }
            }
        };

        checkAdminStatus();
        return () => { cancelled = true; };
    }, []);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center text-sm text-slate-600">
                {t('common.loading')}
            </div>
        );
    }

    if (!isAdmin) {
        return <Navigate to="/signin" replace state={{ from: '/super-admin' }} />;
    }

    return children;
};

export default ProtectedAdminRoute;
